<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBRL } from 'dashboard/helper/lais/currency';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';

defineProps({
  installments: { type: Array, default: () => [] },
  netCents: { type: Number, default: 0 },
  totalCents: { type: Number, default: 0 },
  error: { type: String, default: '' },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const periods = computed(() => [
  {
    id: 'assinatura_recibo_sinal',
    label: t('REAL_ESTATE_COMMISSION_PLAN.PERIODS.RECEIPT'),
  },
  {
    id: 'retirada_certidoes',
    label: t('REAL_ESTATE_COMMISSION_PLAN.PERIODS.CERTIFICATES'),
  },
  { id: 'ato_escritura', label: t('REAL_ESTATE_COMMISSION_PLAN.PERIODS.DEED') },
  {
    id: 'transferencia_registros',
    label: t('REAL_ESTATE_COMMISSION_PLAN.PERIODS.REGISTRY'),
  },
]);
</script>

<template>
  <LaisCard
    class="mt-5"
    :title="t('REAL_ESTATE_COMMISSION_PLAN.INSTALLMENTS.TITLE')"
    prefix-icon="i-lucide-calendar-clock"
  >
    <div class="grid gap-4">
      <div
        v-for="(installment, index) in installments"
        :key="index"
        class="grid gap-4 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4 md:grid-cols-2"
      >
        <LaisField
          :label="
            installments.length > 1
              ? t('REAL_ESTATE_COMMISSION_PLAN.INSTALLMENTS.VALUE', {
                  number: index + 1,
                })
              : t('REAL_ESTATE_COMMISSION_PLAN.INSTALLMENTS.SINGLE_VALUE')
          "
          required
        >
          <input
            :value="installment.amount"
            inputmode="decimal"
            class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
            :placeholder="t('REAL_ESTATE_COMMISSION_PLAN.PLACEHOLDERS.MONEY')"
            @input="emit('update', index, 'amount', $event.target.value)"
          />
        </LaisField>
        <LaisField
          :label="t('REAL_ESTATE_COMMISSION_PLAN.INSTALLMENTS.PERIOD')"
          required
        >
          <select
            :value="installment.paymentTrigger"
            class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
            @change="
              emit('update', index, 'paymentTrigger', $event.target.value)
            "
          >
            <option value="">
              {{ t('REAL_ESTATE_COMMISSION_PLAN.PERIODS.SELECT') }}
            </option>
            <option
              v-for="period in periods"
              :key="period.id"
              :value="period.id"
            >
              {{ period.label }}
            </option>
          </select>
        </LaisField>
      </div>
    </div>
    <p v-if="error" class="mt-4 text-sm text-lais-danger">{{ error }}</p>
    <div
      class="mt-5 grid gap-2 rounded-lais border border-lais-border-soft bg-white p-4 text-sm sm:grid-cols-2"
    >
      <span class="text-lais-text-muted">
        {{ t('REAL_ESTATE_COMMISSION_PLAN.INSTALLMENTS.TOTAL') }}
      </span>
      <strong class="text-lais-text">
        {{ formatBRL(totalCents / 100, { fractionDigits: 2 }) }}
      </strong>
      <span class="text-lais-text-muted">
        {{ t('REAL_ESTATE_COMMISSION_PLAN.INSTALLMENTS.NET_TOTAL') }}
      </span>
      <strong class="text-lais-primary">
        {{ formatBRL(netCents / 100, { fractionDigits: 2 }) }}
      </strong>
    </div>
  </LaisCard>
</template>
